// @flow

import React from 'react';
import { toModifiers } from 'bemify-js';
import { withRouter } from 'next/router';
import { find, flow } from 'lodash';

import { SIGNUP_PRE_INTENT_EVENT, useTrackingContext } from '@zapier/tracking';

import ContentHeader from 'web-ssr/app/common/components/ContentHeader/ContentHeader';
import HeaderAccountActions from 'web-ssr/app/common/components/ContentHeader/HeaderAccountActions';
import ServicesSearchContainer from 'web-ssr/app/common/components/ServicesSearch';
import StatefulHeader from 'web-ssr/app/common/components/ContentHeader/StatefulHeader';
import headerMediaQueries from 'web-ssr/app/common/components/ContentHeader/HeaderBlock.scss';
import matchesMediaQuery from 'web-ssr/app/common/components/matchesMediaQuery';
import megaMenuLinks from 'web-ssr/app/common/components/ContentHeader/mega-menu-links.json';
import { GLOBAL_APP_COUNT } from 'web-ssr/app/common/constants';
import { HeaderContext } from 'web-ssr/app/common/components/ContentHeader/HeaderProvider';
import { createAppIntegrationsPageLink } from 'web-ssr/app/app-directory/utils';
import { trackAndNavigate } from 'web-ssr/app/common/tracking/Track';

import type { HeaderMediaQueries } from 'web-ssr/app/common/components/ContentHeader/types';
import type { RouterProps } from 'web-ssr/app/common/next/types';

type Props = {
  router: RouterProps,
  matchesMediaQuery: HeaderMediaQueries,
  isDesignSystemHeader?: boolean,
  isLoggedIn: boolean,
  data: {
    account?: Object,
    profile?: Object,
  },
  onClickSignup: ?(e: SyntheticEvent<HTMLAnchorElement>) => void,
};

const links = [
  {
    title: 'Home',
    href: '/apps/integrations/',
    kind: toModifiers({ selectable: true }),
    // `relatedMatches` will enable `active` link styling when on a matched
    // route. In this case, it highlights for all the subnav pages.
    relatedMatches: ['/app/connections/', '/app/zaps/', '/app/history/'],
    isVisible: ({ isLoggedIn }) => isLoggedIn,
  },
  {
    title: 'Apps',
    href: '/apps', // make sure no trailing slash to avoid a redirect
    kind: toModifiers({ selectable: true }),
    relatedMatches: ['/apps/categories/'],
    isVisible: meta => meta && true,
    childLinks: find(megaMenuLinks, collection => collection.key === '/apps'),
  },
  {
    title: 'Explore',
    // The query string here is a temporary hack so that the link doesn't
    // highlight both `Home` and `Integrations` when on `/apps/integrations`.
    // In the near future, `Home` will be represented by a different page.
    href: '/apps/integrations/?minimal=true',
    kind: toModifiers({ selectable: true }),
    relatedMatches: [],
    isVisible: meta => meta && true,
    childLinks: find(
      megaMenuLinks,
      collection => collection.key === '/apps/integrations'
    ),
  },
  {
    title: 'Tips & Advice',
    href: '/blog/',
    kind: toModifiers({ selectable: true }),
    relatedMatches: ['/learn/'],
    isVisible: meta => meta && true,
    childLinks: find(megaMenuLinks, collection => collection.key === '/blog'),
  },
  {
    title: 'Pricing',
    href: '/pricing', // make sure no trailing slash to avoid a redirect
    kind: toModifiers({ selectable: true }),
    relatedMatches: [],
    isVisible: ({ isLoggedIn }) => !isLoggedIn,
  },
];

const ContentHeaderContainer = (props: Props) => {
  const { router } = props;
  const { trackEvent } = useTrackingContext();

  const currentUrl = router.asPath;

  const defaultOnClickSignup = e => {
    trackAndNavigate(() =>
      trackEvent(SIGNUP_PRE_INTENT_EVENT, {
        signupPosition: 'header',
      })
    )(e);
  };

  const onClickSignup = props.onClickSignup || defaultOnClickSignup;

  const navigationActions = [
    {
      title: 'Log In',
      href: '/app/login/',
      kind: toModifiers({ divider: true, 'secondary-button': true }),
      relatedMatches: [],
      isVisible: ({ isLoggedIn }) => !isLoggedIn,
    },
    {
      title: 'Sign Up',
      href: '/sign-up/',
      kind: toModifiers({ 'cta-button': true }),
      relatedMatches: [],
      onClick: onClickSignup,
      isVisible: ({ isLoggedIn }) => {
        return !isLoggedIn;
      },
    },
    {
      title: 'Invite Team',
      href: '/app/settings/team/',
      kind: toModifiers({ divider: true, 'secondary-button': true }),
      relatedMatches: [],
      isVisible: ({ isLoggedIn, hasTeamAccount, mediaQueries }) => {
        // Hide this link when on tablet sized media query
        const isOnTabletView = Boolean(
          mediaQueries.tabletMediaQuery && !mediaQueries.desktopMediaQuery
        );

        return isLoggedIn && !isOnTabletView && hasTeamAccount;
      },
    },
    {
      title: 'Upgrade Now',
      href: '/app/billing/plans/',
      kind: toModifiers({ divider: true, 'secondary-button': true }),
      relatedMatches: [],
      isVisible: ({ isLoggedIn, hasTeamAccount, mediaQueries }) => {
        // Hide this link when on tablet sized media query
        const isOnTabletView = Boolean(
          mediaQueries.tabletMediaQuery && !mediaQueries.desktopMediaQuery
        );

        return isLoggedIn && !isOnTabletView && !hasTeamAccount;
      },
    },
    {
      title: 'Make a Zap!',
      href: '/app/editor/',
      kind: toModifiers({ 'cta-button': true }),
      relatedMatches: [],
      isVisible: ({ isLoggedIn }) => isLoggedIn,
    },
  ];

  return (
    <HeaderContext.Consumer>
      {({ isLoggedIn, hasTeamAccount }) => {
        const visibleLinks = links.filter(link =>
          link.isVisible({
            isLoggedIn,
            hasTeamAccount,
            mediaQueries: props.matchesMediaQuery,
          })
        );
        const visibleNavigationActions = navigationActions.filter(action =>
          action.isVisible({
            isLoggedIn,
            hasTeamAccount,
            mediaQueries: props.matchesMediaQuery,
          })
        );

        return (
          <StatefulHeader>
            {renderProps => {
              return (
                <ContentHeader
                  actions={visibleNavigationActions}
                  currentUrl={currentUrl}
                  isDesignSystemHeader={props.isDesignSystemHeader}
                  isFullWidth={false}
                  isLoggedIn={isLoggedIn}
                  isMenuVisible={renderProps.isMenuVisible}
                  isServiceSearchVisible={renderProps.isServiceSearchVisible}
                  links={visibleLinks}
                  matchesMediaQuery={props.matchesMediaQuery}
                  onClickSignup={onClickSignup}
                  onCloseServiceSearch={renderProps.onCloseServiceSearch}
                  onHideMenu={renderProps.onHideMenu}
                  onMount={() => {}}
                  onOpenServiceSearch={renderProps.onOpenServiceSearch}
                  onRevealMenu={renderProps.onRevealMenu}
                  onToggleMenu={renderProps.onToggleMenu}
                  renderAccountActions={() =>
                    !isLoggedIn ? null : <HeaderAccountActions />
                  }
                  renderServicesSearch={() => {
                    return (
                      <ServicesSearchContainer
                        ariaLabel="Search apps"
                        excludeUpcoming={false}
                        isVisible={
                          (renderProps.isServiceSearchVisible && isLoggedIn) ||
                          !isLoggedIn
                        }
                        kind={{
                          slim: true,
                          overlay: isLoggedIn,
                          ...(!renderProps.isServiceSearchVisible && isLoggedIn
                            ? { hidden: true }
                            : { visible: true }),
                        }}
                        onFocusOut={renderProps.onCloseServiceSearch}
                        onHideResults={serviceFound =>
                          !serviceFound &&
                          trackEvent('Zapbook: Header search closed', {
                            ssr: true,
                          })
                        }
                        onSelectService={service => {
                          if (service && service.slug) {
                            trackEvent(
                              'Zapbook: Header search result selected',
                              { service: service.slug, ssr: true }
                            );

                            router.push(
                              createAppIntegrationsPageLink(service.slug)
                            );
                          }
                        }}
                        onShowResults={() =>
                          trackEvent('Zapbook: Header search opened', {
                            ssr: true,
                          })
                        }
                        placeholder={`Search ${GLOBAL_APP_COUNT}+ apps`}
                        showEmptyResults={true}
                      />
                    );
                  }}
                  showServicesSearch={true}
                />
              );
            }}
          </StatefulHeader>
        );
      }}
    </HeaderContext.Consumer>
  );
};

export default flow(
  withRouter,
  matchesMediaQuery(headerMediaQueries)
)(ContentHeaderContainer);
